import { useCallback, useEffect, useState } from "react";

import {
    applyReconciliationResults,
    purgeExactUploadedRecordings,
    reconcileRecordings,
    type ReconciledRecoverableRecording,
} from "~/features/recording-recovery/utils/reconcile-recordings";
import { readPendingCleanupIds } from "~/hooks/media/pending-local-cleanup";
import {
    listRecoverableRecordings,
    subscribeToRecoverableRecordingsChanged,
} from "~/hooks/media/recoverable-recordings";
import {
    ENABLE_RECORDING_CHUNK_RECOVERY_FLAG,
    useFeatureFlag,
} from "~/hooks/use-account-feature-flags";
import { useApiClient } from "~/hooks/use-api-client";
import { useUserProfile } from "~/lib/user";

// AIDEV-NOTE: Client-only hook behind the nav pip + recovery banners. Refreshes
// on mount, tab focus/visibility, and the shared changed-event (fired after a
// retry upload or delete). No polling — IndexedDB reads are cheap but the
// signal only changes on user action or another tab's activity.

export interface UseRecoverableRecordingsResult {
    recordings: ReconciledRecoverableRecording[];
    count: number;
    isLoaded: boolean;
    refresh: () => Promise<void>;
}

export function useRecoverableRecordings(): UseRecoverableRecordingsResult {
    const [recordings, setRecordings] = useState<
        ReconciledRecoverableRecording[]
    >([]);
    const [isLoaded, setIsLoaded] = useState(false);
    const apiClient = useApiClient();
    // AIDEV-NOTE: Recordings stamped with recordedByUserId are only surfaced
    // to that user (shared-browser protection). Shares the "user" query cache
    // already used by the nav, so this adds no extra fetch.
    const { data: userProfile } = useUserProfile();
    const currentUserId = userProfile?.id ?? null;
    // AIDEV-NOTE: Fail-closed rescue flag. Only flagged practitioners pay for the
    // full chunk-store scan that rebuilds meta for stranded chunks.
    const repairOrphanedChunks = useFeatureFlag(
        ENABLE_RECORDING_CHUNK_RECOVERY_FLAG,
    );

    const refresh = useCallback(async () => {
        const next = await listRecoverableRecordings({
            currentUserId,
            repairOrphanedChunks,
        });
        setRecordings((current) =>
            next.map((recording) => {
                const previous = current.find(
                    (candidate) =>
                        candidate.kind === recording.kind &&
                        candidate.id === recording.id,
                );
                return previous?.reconciliation
                    ? {
                          ...recording,
                          reconciliation: previous.reconciliation,
                      }
                    : recording;
            }),
        );
        setIsLoaded(true);
    }, [currentUserId, repairOrphanedChunks]);

    useEffect(() => {
        let cancelled = false;

        const safeRefresh = (shouldReconcile = false) => {
            void listRecoverableRecordings({
                currentUserId,
                repairOrphanedChunks,
            }).then((next) => {
                if (cancelled) return;
                setRecordings((current) =>
                    shouldReconcile
                        ? next
                        : next.map((recording) => {
                              const previous = current.find(
                                  (candidate) =>
                                      candidate.kind === recording.kind &&
                                      candidate.id === recording.id,
                              );
                              return previous?.reconciliation
                                  ? {
                                        ...recording,
                                        reconciliation: previous.reconciliation,
                                    }
                                  : recording;
                          }),
                );
                setIsLoaded(true);

                if (!shouldReconcile || next.length === 0) return;
                void reconcileRecordings(apiClient, next, currentUserId).then(
                    async (results) => {
                        if (cancelled || results.size === 0) return;
                        const purgedKeys = await purgeExactUploadedRecordings(
                            next,
                            results,
                        );
                        if (cancelled) return;
                        setRecordings((current) =>
                            applyReconciliationResults(
                                current,
                                results,
                                purgedKeys,
                            ),
                        );
                    },
                );
            });
        };

        // Reconcile once on mount. Focus/visibility only refresh local storage;
        // the explicit changed event is the only subsequent network trigger.
        safeRefresh(true);

        const handleVisibility = () => {
            if (document.visibilityState === "visible") safeRefresh();
        };

        const handleFocus = () => safeRefresh(false);
        window.addEventListener("focus", handleFocus);
        document.addEventListener("visibilitychange", handleVisibility);
        const unsubscribe = subscribeToRecoverableRecordingsChanged(() => {
            // AIDEV-NOTE: Prune pending-cleanup ids synchronously (localStorage,
            // no IDB) before the async re-list. After a timed-out WebKit delete
            // the readwrite tx is still wedged, so the re-list can queue behind
            // it — without this the stale row stays visible and actionable.
            const pending = new Set(readPendingCleanupIds());
            if (pending.size > 0) {
                setRecordings((current) =>
                    current.filter(
                        (recording) =>
                            recording.kind !== "durable" ||
                            !pending.has(recording.id),
                    ),
                );
            }
            safeRefresh(true);
        });

        return () => {
            cancelled = true;
            window.removeEventListener("focus", handleFocus);
            document.removeEventListener("visibilitychange", handleVisibility);
            unsubscribe();
        };
    }, [apiClient, currentUserId, repairOrphanedChunks]);

    return { recordings, count: recordings.length, isLoaded, refresh };
}
